import React, { useEffect, useState } from 'react';
import { X, Facebook, Instagram, Send, Sparkles, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { BoltLoader } from '@/components/BoostaBolt';
import { useAuth } from '@/contexts/AuthContext';
import { NeonSelect } from '@/components/NeonSelect';

export const PublishDialog = ({ creative, onClose, onPublished }) => {
  const { user } = useAuth();
  const canPublish = !!user?.features?.publish;
  const canSchedule = !!user?.features?.schedule;
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [pages, setPages] = useState([]);
  const [platform, setPlatform] = useState('instagram');
  const [pageId, setPageId] = useState('');
  const [caption, setCaption] = useState('');
  const [genCaption, setGenCaption] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  const isVideo = creative?.type === 'video';
  const videoNotReady = isVideo && creative?.status !== 'ready';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/business/meta/targets');
        setConnected(data.connected);
        setPages(data.pages || []);
        if (data.pages?.length) setPageId(data.pages[0].id);
        if (!data.pages?.some(p => p.has_instagram)) setPlatform('facebook');
      } catch (e) { console.error('Load targets failed', e); }
      finally { setLoading(false); }
      generateCaption();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateCaption = async () => {
    setGenCaption(true);
    try {
      const { data } = await api.post('/ai/caption', { prompt: creative.prompt, platform });
      setCaption(data.caption);
    } catch (e) { console.error('AI caption failed', e); }
    finally { setGenCaption(false); }
  };

  const selectedPage = pages.find(p => p.id === pageId);
  const igUnavailable = platform === 'instagram' && selectedPage && !selectedPage.has_instagram;

  const publish = async () => {
    if (!pageId) { toast.error('Select a Page.'); return; }
    if (!caption.trim()) { toast.error('Add a caption.'); return; }
    if (schedule && !scheduleAt) { toast.error('Pick a date & time to schedule.'); return; }
    setPublishing(true);
    try {
      const body = { creative_id: creative.creative_id, platform, page_id: pageId, caption };
      if (schedule && scheduleAt) body.scheduled_at = new Date(scheduleAt).toISOString();
      const { data } = await api.post('/social/publish', body);
      if (data.status === 'scheduled') toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()} 🗓️`);
      else toast.success(`Posted to ${platform === 'facebook' ? 'Facebook Page' : 'Instagram'} 🎉`);
      onPublished && onPublished();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const minDateTime = new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="publish-dialog">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-white/10 p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="publish-close"><X className="w-5 h-5"/></button>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-1">Publish creative</div>
        <h2 className="font-display font-black text-2xl text-3d mb-5">Post to social</h2>

        <div className="flex gap-4">
          <div className="w-32 shrink-0">
            <div className="aspect-square rounded-xl overflow-hidden bg-black/40 flex items-center justify-center">
              {isVideo ? (
                creative.status === 'ready' && creative.video_url
                  ? <video src={creative.video_url} className="w-full h-full object-cover" muted/>
                  : <div className="text-[10px] text-boosta-cyan px-2 text-center">Rendering…</div>
              ) : <img src={creative.image_url} alt="" className="w-full h-full object-cover"/>}
            </div>
            <div className="text-[10px] text-white/45 mt-1.5 line-clamp-2">{creative.prompt}</div>
          </div>

          <div className="flex-1 space-y-4">
            {loading ? (
              <div className="py-8 flex justify-center"><BoltLoader size={28} label="Loading channels"/></div>
            ) : !canPublish ? (
              <div className="rounded-xl border border-boosta-yellow/25 bg-boosta-yellow/5 p-4 text-sm text-white/70 flex items-start gap-2" data-testid="publish-upgrade">
                <AlertTriangle className="w-4 h-4 text-boosta-yellow mt-0.5"/>
                Publishing to social is available on the <span className="text-boosta-cyan font-bold">Growth</span> plan and above. <a href="/payments" className="text-boosta-cyan underline">Upgrade your plan</a> to unlock it.
              </div>
            ) : !connected ? (
              <div className="rounded-xl border border-boosta-yellow/25 bg-boosta-yellow/5 p-4 text-sm text-white/70 flex items-start gap-2" data-testid="publish-not-connected">
                <AlertTriangle className="w-4 h-4 text-boosta-yellow mt-0.5"/>
                Meta isn't connected. Connect Facebook &amp; Instagram in <span className="text-boosta-cyan">Business Hub</span> to publish.
              </div>
            ) : (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5 block">Platform</label>
                  <div className="flex gap-2">
                    <button onClick={()=>setPlatform('instagram')} data-testid="platform-instagram"
                      className={`flex-1 rounded-xl py-2.5 text-sm inline-flex items-center justify-center gap-2 border transition ${platform==='instagram'?'bg-boosta-cyan/15 border-boosta-cyan/40 text-white':'border-white/10 text-white/55 hover:text-white'}`}>
                      <Instagram className="w-4 h-4"/> Instagram
                    </button>
                    <button onClick={()=>setPlatform('facebook')} data-testid="platform-facebook"
                      className={`flex-1 rounded-xl py-2.5 text-sm inline-flex items-center justify-center gap-2 border transition ${platform==='facebook'?'bg-boosta-cyan/15 border-boosta-cyan/40 text-white':'border-white/10 text-white/55 hover:text-white'}`}>
                      <Facebook className="w-4 h-4"/> Facebook
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5 block">Page</label>
                  <NeonSelect value={pageId} onChange={e=>setPageId(e.target.value)} data-testid="publish-page"
                    options={pages.map(p => ({value:p.id, label:`${p.name}${p.has_instagram?` · IG @${p.ig_username||'linked'}`:''}`}))}/>
                  {igUnavailable && <div className="text-[11px] text-boosta-yellow mt-1.5">This Page has no linked Instagram Business account.</div>}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-[10px] uppercase tracking-wider text-white/45">Caption</label>
                    <button onClick={generateCaption} disabled={genCaption} data-testid="publish-regen-caption" className="text-[11px] text-boosta-cyan hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                      <Sparkles className="w-3 h-3"/> {genCaption?'Writing…':'Regenerate with AI'}
                    </button>
                  </div>
                  <textarea value={caption} onChange={e=>setCaption(e.target.value)} rows="5" data-testid="publish-caption"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm resize-none focus:border-boosta-cyan/50 outline-none"
                    placeholder="Write a caption…"/>
                </div>

                {videoNotReady && <div className="text-[11px] text-boosta-yellow">Video still rendering — wait until it's ready to publish.</div>}

                <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <label className={`flex items-center gap-2 text-sm ${canSchedule?'text-white/75 cursor-pointer':'text-white/40 cursor-not-allowed'}`} data-testid="schedule-toggle-label">
                    <input type="checkbox" checked={schedule} disabled={!canSchedule} onChange={e=>setSchedule(e.target.checked)} data-testid="schedule-toggle" className="accent-boosta-cyan"/>
                    <Clock className="w-3.5 h-3.5 text-boosta-cyan"/> Schedule for later
                    {!canSchedule && <a href="/payments" className="ml-1 text-[11px] text-boosta-yellow underline">Growth+</a>}
                  </label>
                  {schedule && canSchedule && (
                    <input type="datetime-local" value={scheduleAt} min={minDateTime} onChange={e=>setScheduleAt(e.target.value)} data-testid="schedule-datetime"
                      className="mt-3 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:border-boosta-cyan/50 outline-none [color-scheme:dark]"/>
                  )}
                </div>

                <button onClick={publish} disabled={publishing || igUnavailable || videoNotReady} data-testid="publish-submit"
                  className="w-full btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  {publishing ? <BoltLoader size={20} label={schedule?'Scheduling':'Publishing'}/> : (schedule ? <><Clock className="w-4 h-4"/> Schedule post</> : <><Send className="w-4 h-4"/> Publish now</>)}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
